
import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Calendar, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { getRecentBlogPosts } from "@/data/blogData";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useTranslation } from "@/hooks/useTranslation";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDate } from "@/utils/dateFormatter";

const BlogPreview = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const blogPosts = getRecentBlogPosts(3, language);

  return (
    <section className="section-padding bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            {t("blogPreview.title")}
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            {t("blogPreview.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          {blogPosts.map((post, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-md transition-shadow border-gray-100">
              <div className="h-40 sm:h-48 overflow-hidden">
                <img 
                  src={post.featuredImage} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                  <div className="flex items-center">
                    <Calendar size={14} className="mr-1 sm:mr-2" />
                    <span>{formatDate(post.date, language)}</span>
                  </div>
                  <div className="flex items-center text-brand-600">
                    <Clock size={14} className="mr-1" />
                    <span>{post.readTime} {t("blog.readTime")}</span>
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">
                  <Link 
                    to={post.slug} 
                    className="hover:text-brand-600 transition-colors"
                  >
                    {post.title}
                  </Link>
                </h3>
                <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base line-clamp-2">{post.excerpt}</p>
                
                {/* Author info */}
                <div className="flex items-center mb-3 sm:mb-4">
                  <Avatar className="h-7 w-7 sm:h-8 sm:w-8 mr-2">
                    <AvatarImage src="/Joakim_Rounded.webp" alt={post.author.name} />
                    <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-xs sm:text-sm font-medium">{post.author.name}</p>
                    {post.author.role && (
                      <p className="text-[10px] sm:text-xs text-gray-500">{post.author.role}</p>
                    )}
                  </div>
                </div>
                
                <Link 
                  to={post.slug} 
                  className="text-brand-600 font-medium hover:underline flex items-center text-sm sm:text-base"
                >
                  {t("blogPreview.readMore")} →
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center">
          <Link to="/blog">
            <Button variant="outline">{t("blogPreview.viewAll")}</Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
